/**
 * Camera-based barcode / QR scanner for adding products to a new
 * quotation, order, or inventory adjustment from a phone.
 *
 * Decoder: zxing-wasm — the original ZXing C++ library compiled to
 * WebAssembly. Substantially more accurate than the pure-JS ZXing
 * port on marginal-quality 1D prints (especially CODE-39 at typical
 * phone-camera resolutions, where the JS port was giving up and
 * external scanner apps decode instantly).
 *
 * Pipeline is hand-rolled (zxing-wasm is a frame decoder, not a
 * video pipeline):
 *   1. getUserMedia → MediaStream wired to a <video> element we render
 *   2. setTimeout loop at ~10 FPS draws each frame to an offscreen
 *      canvas and hands the ImageData to readBarcodes()
 *   3. On a hit: pause the gate, run /barcode/lookup, either show the
 *      confirm step or (autoAdd mode) auto-append and resume.
 *
 * Stable behaviour:
 *   - The <video> element stays mounted for the life of the dialog;
 *     display:none hides it during the confirm view. The stream keeps
 *     running so "Add & scan next" resumes instantly with no black
 *     flash.
 *   - Between scans we gate readBarcodes with `decodeLockedRef`
 *     instead of stopping the loop. Camera LED stays on the whole
 *     session.
 *   - Full stop() of the tracks is reserved for dialog close, to
 *     actually release the camera and turn the LED off.
 *
 * All user-facing error text is Hebrew; backend English messages are
 * discarded and mapped to Hebrew by HTTP status.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { readBarcodes, prepareZXingModule } from 'zxing-wasm/reader';
import API from '../../services/api';
import MobileDialog from './MobileDialog';

// Kick off the WASM fetch as soon as the module loads so the first
// time the user opens the scanner, the decoder is already warm. The
// library defaults to a jsDelivr CDN URL; prepareZXingModule without
// args uses those defaults but starts the fetch.
prepareZXingModule();

// The formats we ask the decoder to try on each frame. zxing-wasm
// format strings are: "Code39", "Code128", "EAN13", "EAN8", "UPCA",
// "UPCE", "ITF", "Codabar", "Code93", "QRCode", "DataMatrix".
const SUPPORTED_FORMATS = [
  'QRCode',
  'EAN13',
  'EAN8',
  'UPCA',
  'UPCE',
  'Code128',
  'Code39',
  'Code93',
  'ITF',
  'Codabar',
  'DataMatrix',
];

// Per-frame options. tryHarder/tryRotate/tryInvert are all true by
// default in zxing-wasm but we pass them explicitly so the critical
// hints are obvious at a glance — they are what makes CODE-39 decode
// on marginal phone-camera captures, which is the whole reason we
// moved off @zxing/browser (pure JS port) to zxing-wasm (actual C++
// ZXing compiled to WebAssembly).
//
// The two *non*-default overrides target CODE-39 specifically:
//   - minLineCount: 1 (default 2). CODE-39 has no check digit, so
//     zxing waits for two agreeing scan lines before committing —
//     on a hand-held phone this often never converges. Dropping to 1
//     accepts a single clean decode, cutting catch time from "never"
//     to "instant" for the common case. EAN/UPC keep their own
//     check-digit validation regardless, so false positives stay
//     rare in practice.
//   - tryDownscale: false. Our aim-region crop (~1766×475) triggers
//     the default downscale-by-3 path, which merges narrow CODE-39
//     bars and makes decoding worse, not better, at this size.
const READER_OPTIONS = {
  formats: SUPPORTED_FORMATS,
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: false,
  minLineCount: 1,
  maxNumberOfSymbols: 1,
};

// Aim-rectangle geometry in normalized (0..1) coordinates. Shared
// between the visual CSS overlay and the decoder-input crop so what
// the user aims at is precisely what zxing-wasm sees. A generous box
// (~92% × ~44%) that still leaves visible quiet zones around a
// typical label, and cropping the decode surface to this roughly
// halves the pixel count zxing-wasm has to chew on per frame — which
// is the biggest single speedup available for CODE-39.
const AIM_LEFT = 0.04;
const AIM_TOP = 0.28;
const AIM_WIDTH = 0.92;
const AIM_HEIGHT = 0.44;

// Map an axios error to a Hebrew user-facing message. Returns an
// object so the caller can render the Hebrew sentence (RTL) and the
// raw barcode value (LTR) in separate nodes; embedding the barcode
// inline inside the Hebrew string lets the bidi algorithm jumble
// the two halves of the alert.
const lookupErrorFor = (err, barcode) => {
  if (!err?.response) return { message: 'שגיאת רשת. נסה שוב.' };
  const s = err.response.status;
  if (s === 404) return { message: 'לא נמצא מוצר לברקוד:', barcode };
  if (s === 403) return { message: 'סריקת ברקוד אינה מאופשרת עבור הקבוצה' };
  if (s === 400) return { message: 'ברקוד לא תקין:', barcode };
  return { message: 'שגיאה בחיפוש הברקוד:', barcode };
};

// Map a getUserMedia / @zxing failure to a Hebrew message.
const cameraErrorMessage = (err) => {
  const name = err?.name || '';
  const msg = err?.message || String(err || '');
  if (name === 'NotAllowedError' || /Permission|NotAllowed/i.test(msg)) {
    return 'הגישה למצלמה נחסמה. יש לאפשר גישה או להקליד ברקוד ידנית.';
  }
  if (name === 'NotFoundError' || /NotFound|no camera/i.test(msg)) {
    return 'לא נמצאה מצלמה במכשיר. ניתן להקליד ברקוד ידנית.';
  }
  if (name === 'NotReadableError' || /in use/i.test(msg)) {
    return 'המצלמה בשימוש ביישום אחר. סגור אותו ונסה שוב.';
  }
  if (name === 'SecurityError') {
    return 'לא ניתן להפעיל מצלמה ללא חיבור מאובטח (HTTPS).';
  }
  if (name === 'OverconstrainedError') {
    return 'לא ניתן להפעיל את המצלמה האחורית. ניתן להקליד ברקוד ידנית.';
  }
  return 'לא ניתן להפעיל את המצלמה. ניתן להקליד ברקוד ידנית.';
};

const BarcodeScannerDialog = ({
  open,
  onClose,
  onAdd,
  productLabel = 'מוצר',
  // When true, skip the qty/price confirm step and auto-call onAdd with
  // quantity=1 on every successful scan. Used by the inventory panel
  // where the user sets the adjustment quantity later in a separate list.
  autoAdd = false,
  // autoAdd-only: when true, close the dialog after each successful add
  // instead of resuming the camera. Right for "add to an order" flows
  // where the operator wants to see the new row land in the pending
  // items table before scanning the next one — scanning silently in
  // the background made them think the scan didn't register and they
  // ended up scanning the same item 4–5 times in a row. Keep false for
  // the inventory page's rapid-fire shelf-scan workflow.
  closeOnAdd = false,
  // When provided, the dialog skips the /barcode/lookup/ flow entirely
  // and just hands the raw decoded text back to the caller (then closes).
  // Used by the Products page for filling the search field — no need to
  // resolve to a product first; the page's existing search filter does
  // that work and gives us a useful "no results" view if the value
  // doesn't match anything.
  onScanText = null,
}) => {
  // We drive the camera + decode loop ourselves since zxing-wasm is a
  // frame decoder, not a video pipeline. `videoRef` holds the <video>,
  // `streamRef` the active MediaStream (so we can stop tracks and
  // release the camera LED), `canvasRef` an offscreen 2D canvas we
  // reuse every frame to grab ImageData for readBarcodes().
  // `decodeLoopRef` is the setTimeout id for the scan loop;
  // `decodeLockedRef` is our "pause" gate while a lookup is in flight
  // or the confirm step is showing.
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  // Video track + zoom capabilities. Many phones support digital zoom
  // via MediaTrack.applyConstraints({ zoom }) — we use it to "get
  // closer" to small printed barcodes without physically bringing the
  // phone inside its minimum focus distance, which is the common
  // cause of blurry captures.
  const videoTrackRef = useRef(null);
  const decodeLoopRef = useRef(null);
  const decodeLockedRef = useRef(false);
  const lookupInFlight = useRef(false);
  // Per-decode throttle: in autoAdd mode the scanner loops continuously,
  // so without this the same barcode would lookup → "not found" → repeat
  // many times a second, stomping the fail banner. Keeps the last
  // successful decode text + timestamp and skips re-lookup for a short
  // window. A different barcode bypasses the throttle immediately.
  const lastScanRef = useRef({ text: '', ts: 0 });
  const [scannerReady, setScannerReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState('');

  const [lookupLoading, setLookupLoading] = useState(false);
  // `lookupError` is either null or { message, barcode? } — rendered as
  // two stacked nodes so the bidi algorithm doesn't tangle the Hebrew
  // message with an LTR barcode value.
  const [lookupError, setLookupError] = useState(null);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [lastAddedName, setLastAddedName] = useState('');
  // autoAdd-mode equivalent of `lastAddedName` but for failures — the
  // `lookupError` state alone flickered during rapid scans because
  // every new decode calls setLookupError(null) at the start. This
  // dedicated banner survives a few seconds after a "not found" so
  // the operator actually sees it.
  const [lastFailedBarcode, setLastFailedBarcode] = useState('');
  // Diagnostic: what the camera last decoded (any read — success or
  // "lookup failed"). Shown on-device so the operator can confirm the
  // camera is reading *something* and compare the value against their
  // product.Barcode without needing Chrome devtools.
  const [lastDecoded, setLastDecoded] = useState(null);
  const [zoomCaps, setZoomCaps] = useState(null); // { min, max, step } or null
  const [zoom, setZoom] = useState(1);
  // Tap-to-focus indicator — { x, y } in CSS pixels relative to the
  // video box, rendered as a brief animated ring. null when hidden.
  const [focusRing, setFocusRing] = useState(null);
  const focusRingTimerRef = useRef(null);
  const lastAddedTimerRef = useRef(null);
  const lastFailedTimerRef = useRef(null);
  const qtyRef = useRef(null);
  const manualInputRef = useRef(null);

  // "Resume" — unlock the gate so the next decode loop tick will try
  // a lookup. The camera itself is still streaming — we never stopped.
  const resumeScanner = useCallback(() => {
    decodeLockedRef.current = false;
  }, []);

  // Tap-to-focus. Uses MediaTrack pointsOfInterest (normalized 0..1
  // coordinates in the camera frame) plus a transient single-shot
  // focusMode; after a beat we flip focusMode back to 'continuous' so
  // passive autofocus resumes. Also flashes a short animated ring at
  // the tap location as user feedback — even on browsers where the
  // camera doesn't honor the constraint, the ring at least confirms
  // the tap registered.
  const handleVideoTap = useCallback(async (e) => {
    const video = videoRef.current;
    if (!video) return;
    const rect = video.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    // Always show the ring for feedback.
    setFocusRing({ x: localX, y: localY });
    if (focusRingTimerRef.current) clearTimeout(focusRingTimerRef.current);
    focusRingTimerRef.current = setTimeout(() => setFocusRing(null), 700);

    const track = videoTrackRef.current;
    if (!track || typeof track.applyConstraints !== 'function') return;
    const x = Math.max(0, Math.min(1, localX / rect.width));
    const y = Math.max(0, Math.min(1, localY / rect.height));
    const caps = (typeof track.getCapabilities === 'function' && track.getCapabilities()) || {};
    const adv = {};
    if (caps.pointsOfInterest) adv.pointsOfInterest = [{ x, y }];
    if (caps.focusMode?.includes?.('single-shot')) {
      adv.focusMode = 'single-shot';
    } else if (caps.focusMode?.includes?.('manual')) {
      adv.focusMode = 'manual';
    }
    if (Object.keys(adv).length === 0) return;
    try {
      // eslint-disable-next-line no-restricted-syntax
      await track.applyConstraints({ advanced: [adv] });
      // Restore continuous autofocus so the camera keeps tracking
      // after the tap's one-shot acquisition.
      if (caps.focusMode?.includes?.('continuous')) {
        setTimeout(() => {
          // eslint-disable-next-line no-restricted-syntax
          track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
        }, 2500);
      }
    } catch (_) { /* */ }
  }, []);

  // Apply a new digital zoom level to the active video track. No-ops
  // when the camera doesn't advertise zoom capabilities.
  const applyZoom = useCallback(async (next) => {
    const track = videoTrackRef.current;
    if (!track || !zoomCaps) return;
    const clamped = Math.max(zoomCaps.min, Math.min(zoomCaps.max, next));
    try {
      // eslint-disable-next-line no-restricted-syntax
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
      setZoom(clamped);
    } catch (_) { /* */ }
  }, [zoomCaps]);

  // Full teardown — cancel the scan loop and stop the camera tracks so
  // the LED turns off. Called on dialog close or manual-entry toggle.
  const stopScanner = useCallback(() => {
    decodeLockedRef.current = false;
    setScannerReady(false);
    if (decodeLoopRef.current) {
      clearTimeout(decodeLoopRef.current);
      decodeLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) { /* */ }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (_) { /* */ }
    }
    videoTrackRef.current = null;
    setZoomCaps(null);
    setZoom(1);
  }, []);

  const handleLookup = useCallback(async (code) => {
    const barcode = (code || '').trim();
    if (!barcode) return false;

    // onScanText short-circuit: caller wants the raw decoded text and
    // will do its own thing with it (e.g. fill a search box). No API
    // call, no confirm step — hand it back and close.
    if (onScanText) {
      onScanText(barcode);
      onClose?.();
      return true;
    }

    setLookupLoading(true);
    setLookupError(null);

    // Two-step resolution:
    // 1. Exact match via /barcode/lookup/ (the designed-for-scan
    //    endpoint).
    // 2. On 404 only, fall back to /products/?search=<barcode>,
    //    which uses icontains across ProductName, CatalogNumber,
    //    and Barcode. This catches the common case where the
    //    stored Barcode has stray whitespace or extra leading/
    //    trailing digits that make the exact match miss. Only
    //    auto-select when exactly one product matches — otherwise
    //    ambiguous and we show an honest error.
    const fetchProductByScan = async () => {
      try {
        const { data } = await API.get('/barcode/lookup/', { params: { barcode } });
        return { ok: true, product: data.product };
      } catch (e) {
        if (e?.response?.status !== 404) return { ok: false, err: e };
        try {
          const { data: list } = await API.get('/products/', {
            params: { light: true, search: barcode },
          });
          const arr = Array.isArray(list) ? list : (list?.results || []);
          if (arr.length === 1) return { ok: true, product: arr[0] };
          if (arr.length > 1) {
            return {
              ok: false,
              err: new Error('ambiguous'),
              ambiguousCount: arr.length,
            };
          }
        } catch (_) { /* fall through to 404 */ }
        return { ok: false, err: e };
      }
    };

    try {
      const result = await fetchProductByScan();
      if (!result.ok) {
        if (result.ambiguousCount) {
          setLookupError({
            message: `נמצאו ${result.ambiguousCount} מוצרים לברקוד — יש לבחור ידנית:`,
            barcode,
          });
        } else {
          setLookupError(lookupErrorFor(result.err, barcode));
        }
        // Flash a persistent banner at the top of the dialog — the
        // lookupError Alert renders below the video and is easily
        // off-screen on a phone, so without this the operator sees
        // nothing and thinks the camera never caught the barcode.
        // Apply to both modes (autoAdd inventory flow AND the
        // confirm-step new-quotation / new-order flow).
        setLastFailedBarcode(barcode);
        setLastAddedName('');
        if (lastFailedTimerRef.current) clearTimeout(lastFailedTimerRef.current);
        lastFailedTimerRef.current = setTimeout(() => setLastFailedBarcode(''), 3500);
        return false;
      }
      const p = result.product;
      if (autoAdd) {
        // Rapid-scan mode — add straight to the parent's list.
        onAdd?.({
          product: p,
          quantity: 1,
          unitPrice: Number(p?.Price) || 0,
        });
        if (closeOnAdd) {
          // Close so the parent's pending-items table becomes visible
          // and the operator sees the row land. Parent reopens the
          // scanner on the next tap.
          onClose?.();
          return true;
        }
        // Rapid-fire mode — stay open, flash the success banner.
        setLastAddedName(p?.ProductName || '');
        setLastFailedBarcode('');
        if (lastFailedTimerRef.current) clearTimeout(lastFailedTimerRef.current);
        if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
        lastAddedTimerRef.current = setTimeout(() => setLastAddedName(''), 2500);
        return true;
      }
      setProduct(p);
      setQuantity('1');
      setUnitPrice(String(p?.Price ?? ''));
      setTimeout(() => {
        qtyRef.current?.focus();
        qtyRef.current?.select?.();
      }, 150);
      return true;
    } catch (e) {
      setLookupError(lookupErrorFor(e, barcode));
      return false;
    } finally {
      setLookupLoading(false);
    }
  }, [autoAdd, closeOnAdd, onAdd, onScanText, onClose]);

  const startScanner = useCallback(async () => {
    if (streamRef.current) return;
    setCameraError('');
    if (!videoRef.current) return;
    try {
      // 1920×1080 is the sweet spot for CODE-39/128 decoding on a
      // phone. Continuous focus lets the user bring the label in and
      // out without tapping to refocus.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          // eslint-disable-next-line no-restricted-syntax
          advanced: [{ focusMode: 'continuous' }],
        },
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      // Probe the video track for zoom support. We do NOT auto-apply
      // a zoom — on many phones switching to 2x at the moment the
      // camera starts pushes the subject inside the (now longer) min
      // focus distance, and small printed labels go blurry exactly
      // when the user expects the scanner to be at its most reliable.
      // Instead expose the ± controls so the operator can zoom in on
      // demand when a barcode is too small at the current distance.
      const track = stream.getVideoTracks()[0];
      videoTrackRef.current = track || null;
      if (track && typeof track.getCapabilities === 'function') {
        try {
          const caps = track.getCapabilities() || {};
          if (caps.zoom) {
            const step = caps.zoom.step || 0.1;
            setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step });
            setZoom(1);
          } else {
            setZoomCaps(null);
            setZoom(1);
          }
        } catch (_) {
          setZoomCaps(null);
        }
      }

      setScannerReady(true);

      // Scan loop — grab a frame every ~100 ms and hand the ImageData
      // to zxing-wasm. 100 ms ≈ 10 FPS which keeps CPU reasonable on
      // mid-range phones while still feeling instantaneous to the
      // operator. If a lookup is in flight we skip the decode entirely.
      const tick = async () => {
        if (!streamRef.current) return; // torn down
        if (decodeLockedRef.current || lookupInFlight.current) {
          decodeLoopRef.current = setTimeout(tick, 100);
          return;
        }
        const video = videoRef.current;
        if (!video || video.readyState < 2 || video.videoWidth === 0) {
          decodeLoopRef.current = setTimeout(tick, 100);
          return;
        }
        try {
          // Crop to the aim rectangle. Using the same normalized
          // coordinates as the CSS overlay means what the operator
          // visually aims at is exactly what the decoder processes —
          // and cuts the per-frame work nearly in half.
          const sx = Math.floor(video.videoWidth * AIM_LEFT);
          const sy = Math.floor(video.videoHeight * AIM_TOP);
          const sw = Math.floor(video.videoWidth * AIM_WIDTH);
          const sh = Math.floor(video.videoHeight * AIM_HEIGHT);
          const canvas = canvasRef.current;
          canvas.width = sw;
          canvas.height = sh;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
          const imageData = ctx.getImageData(0, 0, sw, sh);

          lookupInFlight.current = true;
          const results = await readBarcodes(imageData, READER_OPTIONS);
          if (results && results.length > 0) {
            const r = results[0];
            const text = r.text;
            const formatName = r.format || null;
            const now = Date.now();
            const sameAsLast = text === lastScanRef.current.text
              && now - lastScanRef.current.ts < 2000;
            // eslint-disable-next-line no-console
            console.info('[BarcodeScanner] decoded', { text, format: formatName, sameAsLast });
            setLastDecoded({ text, format: formatName });
            if (!sameAsLast) {
              lastScanRef.current = { text, ts: now };
              decodeLockedRef.current = true;
              const ok = await handleLookup(text);
              // autoAdd: always re-open the gate for rapid-scan flow.
              // confirm-step: stay locked on success (confirm view shows);
              // reopen only on failure so the user can try another barcode.
              if (!ok || autoAdd) {
                decodeLockedRef.current = false;
              }
            }
          }
        } catch (err) {
          // zxing-wasm throws on internal decode errors (not "nothing
          // found"). Log but keep scanning.
          // eslint-disable-next-line no-console
          console.warn('[BarcodeScanner] decode error', err);
        } finally {
          lookupInFlight.current = false;
        }
        decodeLoopRef.current = setTimeout(tick, 100);
      };
      decodeLoopRef.current = setTimeout(tick, 100);
    } catch (err) {
      setCameraError(cameraErrorMessage(err));
      setManualMode(true);
    }
  }, [handleLookup, autoAdd]);

  // Dialog open/close lifecycle.
  useEffect(() => {
    if (!open) {
      stopScanner();
      return undefined;
    }
    setProduct(null);
    setLookupError(null);
    setCameraError('');
    setManualInput('');
    setQuantity('1');
    setUnitPrice('');
    setManualMode(false);
    setLastAddedName('');
    setLastFailedBarcode('');
    setLastDecoded(null);
    lastScanRef.current = { text: '', ts: 0 };

    // Wait a tick so the scanner container is in the DOM.
    const t = setTimeout(() => { startScanner(); }, 200);
    return () => {
      clearTimeout(t);
      if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
      if (lastFailedTimerRef.current) clearTimeout(lastFailedTimerRef.current);
      if (focusRingTimerRef.current) clearTimeout(focusRingTimerRef.current);
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (manualMode && !product) {
      setTimeout(() => manualInputRef.current?.focus(), 100);
    }
  }, [manualMode, product]);

  // Hold onClose in a ref so the back-button effect below only
  // re-runs when `open` itself toggles. Callers pass an inline
  // arrow (`onClose={() => setScannerOpen(false)}`) so its identity
  // changes on every parent render — without this ref, the effect
  // would re-run, fire history.back() in cleanup, and pop an extra
  // URL from the stack, walking the user back past the page they
  // were on.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Back-button handling — push a sentinel history entry while the
  // dialog is open and intercept popstate so the phone's back
  // gesture closes the scanner instead of navigating the page back
  // (which on /order/new would walk the user all the way back to
  // /clients, skipping both the dialog and the page they were
  // working on). Standard pattern for modal dialogs in SPAs.
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ barcodeScannerOpen: true }, '');
    // Tracks whether the back button (popstate) is what closed us.
    // If so, our sentinel is already off the stack and cleanup
    // must NOT call history.back() a second time. If the dialog
    // is closed by a button handler instead, the sentinel is
    // still on the stack and we pop it in cleanup.
    const poppedByBack = { current: false };
    const onPopState = () => {
      poppedByBack.current = true;
      onCloseRef.current?.();
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (!poppedByBack.current) {
        window.history.back();
      }
    };
  }, [open]);

  const handleClose = () => {
    stopScanner();
    onClose?.();
  };

  const handleManualSubmit = () => {
    if (!manualInput.trim() || lookupLoading) return;
    handleLookup(manualInput);
  };

  // Returns true if the add succeeded, false if validation failed.
  const performAdd = () => {
    if (!product) return false;
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      setLookupError({ message: 'נא להזין כמות' });
      return false;
    }
    const price = parseFloat(unitPrice);
    onAdd?.({
      product,
      quantity: qty,
      unitPrice: isNaN(price) ? Number(product.Price) || 0 : price,
    });
    return true;
  };

  const handleAddAndScanNext = () => {
    if (!performAdd()) return;
    setProduct(null);
    setLookupError(null);
    setManualInput('');
    if (!manualMode) resumeScanner();
  };

  const handleAddAndClose = () => {
    if (!performAdd()) return;
    stopScanner();
    onClose?.();
  };

  const handleRetry = () => {
    setProduct(null);
    setLookupError(null);
    setManualInput('');
    if (!manualMode) resumeScanner();
  };

  const handleToggleMode = () => {
    setLookupError(null);
    if (manualMode) {
      setManualMode(false);
      setTimeout(() => startScanner(), 100);
    } else {
      stopScanner();
      setManualMode(true);
    }
  };

  const showScannerView = !product && !manualMode;
  const showConfirmView = !!product;
  const showManualView = !product && manualMode;

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
          <QrCodeScannerIcon color="primary" />
          <Typography variant="h6">סריקת ברקוד</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="סגור">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
        {cameraError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {cameraError}
          </Alert>
        )}

        {autoAdd && lastAddedName && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastAddedName('')}>
            נוסף: {lastAddedName}
          </Alert>
        )}

        {lastFailedBarcode && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setLastFailedBarcode('')}
          >
            <Typography variant="body2">לא נמצא מוצר לברקוד:</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={
                  <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                    {lastFailedBarcode}
                  </span>
                }
              />
            </Box>
          </Alert>
        )}

        {/*
          The <video> element is always mounted so the camera stream isn't
          torn down when we show the confirm view. We gate decoding with
          decodeLockedRef instead of remounting. `display:none` while the
          confirm / manual view is active keeps the camera running but
          hidden — "Add & scan next" resumes instantly with no flash.
        */}
        <Box sx={{ display: showScannerView ? 'block' : 'none' }}>
          <Box
            onClick={handleVideoTap}
            sx={{
              width: '100%',
              minHeight: { xs: 280, sm: 320 },
              bgcolor: '#000',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
            {focusRing && (
              <Box
                sx={{
                  position: 'absolute',
                  left: focusRing.x - 28,
                  top: focusRing.y - 28,
                  width: 56,
                  height: 56,
                  border: '2px solid rgba(255, 235, 59, 0.95)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.4) inset',
                  animation: 'bp-focus-pulse 700ms ease-out',
                  '@keyframes bp-focus-pulse': {
                    '0%': { transform: 'scale(1.4)', opacity: 0 },
                    '30%': { transform: 'scale(1)', opacity: 1 },
                    '100%': { transform: 'scale(0.85)', opacity: 0 },
                  },
                }}
              />
            )}
            {/*
              Decorative aim rectangle — CSS overlay only, does NOT crop
              the decode region. ZXing sees the full frame so CODE-39 /
              CODE-128 with narrow bars still decodes reliably; the
              operator just gets a visual target shaped like a real 1D
              barcode (wide and short, ~4:1 aspect), which matches what
              they're aiming at on paper.
            */}
            {scannerReady && (
              <Box
                sx={{
                  position: 'absolute',
                  top: `${AIM_TOP * 100}%`,
                  left: `${AIM_LEFT * 100}%`,
                  width: `${AIM_WIDTH * 100}%`,
                  height: `${AIM_HEIGHT * 100}%`,
                  border: '2px solid rgba(76, 175, 80, 0.9)',
                  borderRadius: 1,
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.18)',
                }}
              />
            )}
            {/*
              Zoom controls — only shown when the phone's camera
              actually advertises zoom capabilities. Lets the operator
              zoom in on a small printed barcode without moving the
              phone inside its minimum focus distance (which is why
              small labels were going blurry).
            */}
            {scannerReady && zoomCaps && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'rgba(0, 0, 0, 0.55)',
                  borderRadius: 4,
                  px: 0.5,
                  direction: 'ltr',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => applyZoom(zoom - Math.max(zoomCaps.step * 10, 0.5))}
                  disabled={zoom <= zoomCaps.min}
                  sx={{ color: 'white' }}
                  aria-label="הקטן זום"
                >
                  <ZoomOutIcon />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{ color: 'white', minWidth: 36, textAlign: 'center' }}
                >
                  {`${zoom.toFixed(1)}x`}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => applyZoom(zoom + Math.max(zoomCaps.step * 10, 0.5))}
                  disabled={zoom >= zoomCaps.max}
                  sx={{ color: 'white' }}
                  aria-label="הגדל זום"
                >
                  <ZoomInIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          {!scannerReady && !cameraError && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                מפעיל מצלמה...
              </Typography>
            </Box>
          )}
          {scannerReady && (
            <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', color: 'text.secondary' }}>
              כוון את המצלמה אל הברקוד כדי להוסיף {productLabel}
            </Typography>
          )}
          {lastDecoded && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Chip
                size="small"
                variant="outlined"
                label={
                  <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                    {`${lastDecoded.text}${lastDecoded.format ? ` · ${lastDecoded.format}` : ''}`}
                  </span>
                }
                sx={{ maxWidth: '100%' }}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                סריקה אחרונה
              </Typography>
            </Box>
          )}
        </Box>

        {showManualView && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              inputRef={manualInputRef}
              fullWidth
              label="ברקוד"
              placeholder="הקלד ברקוד"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleManualSubmit();
                }
              }}
              InputProps={{ sx: { direction: 'ltr', fontSize: '1.1rem' } }}
            />
            <Button
              variant="contained"
              onClick={handleManualSubmit}
              disabled={!manualInput.trim() || lookupLoading}
              sx={{ minWidth: 90, alignSelf: 'stretch' }}
            >
              חפש
            </Button>
          </Box>
        )}

        {!showConfirmView && lookupLoading && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!showConfirmView && lookupError && !lookupLoading && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">{lookupError.message}</Typography>
            {lookupError.barcode && (
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  size="small"
                  label={
                    <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                      {lookupError.barcode}
                    </span>
                  }
                />
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Button color="inherit" size="small" onClick={handleRetry}>
                נסה שוב
              </Button>
            </Box>
          </Alert>
        )}

        {!showConfirmView && !cameraError && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              size="small"
              startIcon={manualMode ? <QrCodeScannerIcon /> : <KeyboardIcon />}
              onClick={handleToggleMode}
              sx={{ direction: 'ltr' }}
            >
              {manualMode ? 'חזור לסריקה' : 'הזן ברקוד ידנית'}
            </Button>
          </Box>
        )}

        {showConfirmView && (
          <Paper sx={{ p: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {product.ProductName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {product.CatalogNumber && (
                <Chip size="small" label={`מק״ט: ${product.CatalogNumber}`} />
              )}
              {product.Barcode && (
                <Chip size="small" label={product.Barcode} sx={{ direction: 'ltr' }} />
              )}
              {product.Unit && <Chip size="small" label={product.Unit} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                inputRef={qtyRef}
                label="כמות"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  '& input': {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
              />
              <TextField
                label="מחיר"
                type="number"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  direction: 'ltr',
                  '& input': {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">₪</InputAdornment>,
                }}
              />
            </Box>
            {lookupError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">{lookupError.message}</Typography>
                {lookupError.barcode && (
                  <Box sx={{ mt: 0.75 }}>
                    <Chip
                      size="small"
                      label={
                        <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                          {lookupError.barcode}
                        </span>
                      }
                    />
                  </Box>
                )}
              </Alert>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {showConfirmView ? (
          <>
            <Button onClick={handleRetry}>סרוק שוב</Button>
            <Button
              onClick={handleAddAndScanNext}
              startIcon={<AddIcon />}
              sx={{ direction: 'ltr' }}
            >
              הוסף וסרוק עוד
            </Button>
            <Button
              variant="contained"
              onClick={handleAddAndClose}
              startIcon={<AddIcon />}
              sx={{ direction: 'ltr' }}
            >
              הוסף וסגור
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>סגור</Button>
        )}
      </DialogActions>
    </MobileDialog>
  );
};

export default BarcodeScannerDialog;
